/**
 * Activity in nu-assist: every event 03 returns, newest first, one colour per
 * search (the list's search, else the list itself). Only what added something
 * about the person — capture, AI review, Company Map, add to search — plus the
 * two human decisions; a Compare to Thrive is state, not an event.
 */
import { Bot, Download, GitBranch, Send, UserX, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo } from "react";

import type {
  PersonAppearance,
  PersonDetail,
  PersonEvent,
  PersonReviewRun,
} from "@nu-assist/contracts";

import { ScoreBadge } from "../../routes/research/import-detail/ScoreBadge";
import { EmptyLine, PanelSection, TimelineLine } from "./PanelSection";
import { PANEL_PAGE_SIZE, PanelPager, usePagedList } from "./paging";
import {
  fmtDateTime,
  personFirstName,
  searchColours,
  searchKey,
  searchLabel,
  sourceLabel,
} from "./format";

const ICON: Record<PersonEvent["kind"], ReactNode> = {
  captured: <Download className="h-4 w-4" aria-hidden="true" />,
  ai_review: <Bot className="h-4 w-4" aria-hidden="true" />,
  company_map: <GitBranch className="h-4 w-4" aria-hidden="true" />,
  added_to_search: <Send className="h-4 w-4" aria-hidden="true" />,
  left_out: <XCircle className="h-4 w-4" aria-hidden="true" />,
  different_person: <UserX className="h-4 w-4" aria-hidden="true" />,
};

function reviewById(person: PersonDetail): Map<string, PersonReviewRun> {
  const map = new Map<string, PersonReviewRun>();
  for (const a of person.appearances ?? [])
    for (const r of a.ai_reviews ?? []) map.set(r.review_id, r);
  return map;
}

function Chip({ children, dot }: { children: ReactNode; dot?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-pill border border-border px-2 py-0.5 text-caption text-text-secondary">
      {dot && <span className={`h-2 w-2 rounded-full ${dot}`} />}
      {children}
    </span>
  );
}

function EventBody({
  event,
  person,
  appearance,
  reviews,
}: {
  event: PersonEvent;
  person: PersonDetail;
  appearance: PersonAppearance | undefined;
  reviews: Map<string, PersonReviewRun>;
}) {
  const first = personFirstName(person);
  const list = <span className="font-semibold">{event.list_name ?? "a list"}</span>;
  const actor = event.actor ? <span className="font-semibold">{event.actor}</span> : "Someone";
  switch (event.kind) {
    case "captured":
      return (
        <>
          <div className="text-body text-text">
            {event.source === "thrive_search" ? (
              // A list pulled from a Thrive search names the search it was
              // pulled FROM (04), not the one it is linked to.
              <>
                {actor} pulled {first} from Thrive search{" "}
                {event.origin_search_id ?? ""} into {list}
              </>
            ) : (
              <>
                {actor} captured {first} in {list}
              </>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-2">
            <Chip>
              {sourceLabel(event.source)}
              {event.total_contacts != null && ` · ${event.total_contacts} people`}
            </Chip>
          </div>
          {appearance?.search_criteria && (
            <details className="mt-1 text-caption text-text-secondary">
              <summary className="cursor-pointer">Recruiter search filters</summary>
              <p className="mt-1 whitespace-pre-wrap font-mono">{appearance.search_criteria}</p>
            </details>
          )}
        </>
      );
    case "company_map":
      return (
        <div className="text-body text-text">
          {actor} ran a Company Map on {list} that matched {first}
        </div>
      );
    case "ai_review": {
      const run = event.review_id ? reviews.get(event.review_id) : undefined;
      return (
        <>
          <div className="text-body text-text">
            {actor} ran AI review{" "}
            <span className="font-semibold">v{event.version ?? run?.version}</span> on {list}
          </div>
          {run && (
            <details
              className="mt-1 text-caption text-text-secondary"
              data-testid="person-event-review"
            >
              <summary className="cursor-pointer">
                {run.total != null ? run.total.toFixed(2) : "–"} · {(run.cells ?? []).length}{" "}
                criteria · {run.model_version}
              </summary>
              <ul className="mt-2 flex flex-col gap-2">
                {(run.cells ?? []).map((c) => (
                  <li key={c.criterion} className="flex gap-2">
                    <ScoreBadge value={c.score} kind="score" />
                    <span>
                      <span className="font-medium text-text">{c.criterion}</span>
                      {c.rationale && <span className="block">{c.rationale}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      );
    }
    case "added_to_search":
      return <div className="text-body text-text">Added to the search from {list}</div>;
    case "left_out":
      return (
        <div className="text-body text-text">
          {actor} left {first} out of {list}
        </div>
      );
    case "different_person":
      return (
        <div className="text-body text-text">
          {actor} marked Thrive #{event.thrive_contact_id ?? "?"} as a different person
        </div>
      );
    default:
      // A kind this build does not know (an API ahead of the web): say what
      // happened rather than render an empty row.
      return <div className="text-body text-text">{String((event as PersonEvent).kind).replace(/_/g, " ")}</div>;
  }
}

export function ActivityTab({ person }: { person: PersonDetail }) {
  const colours = useMemo(() => searchColours(person), [person]);
  const reviews = useMemo(() => reviewById(person), [person]);
  const appearances = person.appearances ?? [];
  const byImport = new Map(appearances.map((a) => [a.import_id, a]));
  const searches = [...new Map(appearances.map((a) => [searchKey(a), a])).values()];
  const events = useMemo(() => [...(person.events ?? [])].reverse(), [person]);
  const paged = usePagedList(events, person.handle);

  return (
    <PanelSection
      title={`Everything that happened to ${personFirstName(person)} in nu-assist`}
      testId="person-activity"
    >
      {searches.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2" aria-label="Searches in nu-assist">
          {searches.map((a) => (
            <Chip key={searchKey(a)} dot={colours.get(searchKey(a))?.dot}>
              {searchLabel(a)}
            </Chip>
          ))}
        </div>
      )}
      {events.length === 0 ? (
        <EmptyLine>Nothing has been recorded for {personFirstName(person)} yet.</EmptyLine>
      ) : (
        <ol className="flex flex-col gap-4" data-testid="person-activity-timeline">
          {paged.items.map((e, i) => {
            const appearance = e.import_id ? byImport.get(e.import_id) : undefined;
            const ring = appearance ? colours.get(searchKey(appearance))?.ring : undefined;
            return (
              <li
                key={`${e.kind}-${e.at}-${(paged.page - 1) * PANEL_PAGE_SIZE + i}`}
                className="relative flex gap-3"
                data-testid="person-event"
                data-kind={e.kind}
              >
                {i < paged.items.length - 1 && <TimelineLine />}
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 bg-surface text-text-secondary ${ring ?? "border-border"}`}
                >
                  {ICON[e.kind]}
                </span>
                <div className="min-w-0 pt-1">
                  <div className="text-caption tabular-nums text-text-muted">
                    {fmtDateTime(e.at)}
                  </div>
                  <EventBody event={e} person={person} appearance={appearance} reviews={reviews} />
                  {appearance && (
                    <div className="mt-1">
                      <Chip dot={colours.get(searchKey(appearance))?.dot}>
                        {searchLabel(appearance)}
                      </Chip>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <PanelPager paged={paged} noun="event" testId="person-activity-pager" />
    </PanelSection>
  );
}
